import {
    DestroyRef, Directive, inject, OnInit
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { LIST_STATE_SERVICE } from "../constants/tokens";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ProcessingDirective } from "../../application/directives/processing/processing.directive";

/**
 * Directive that connects a MatPaginator to a ListStateService.
 *
 * This directive synchronizes pagination state between the MatPaginator component
 * and the application's list state service. It handles page events from the paginator
 * and updates the state service accordingly, while also reflecting state changes
 * from the service back to the paginator.
 *
 * Usage:
 * ```html
 * <mat-paginator mgdPaginator></mat-paginator>
 * ```
 *
 * Note: This directive automatically includes the ProcessingDirective to handle
 * disabling the paginator during processing states.
 */
@Directive({
    selector: "[mgdPaginator]",
    hostDirectives: [{
        directive: ProcessingDirective,
        inputs: ["disableWhileProcessing"]
    }]
})
export class PaginatorDirective implements OnInit {

    /**
     * Reference to the MatPaginator component this directive is attached to
     */
    readonly paginator = inject(MatPaginator, { self: true });

    /**
     * List state service that manages pagination state
     */
    private readonly _state = inject(LIST_STATE_SERVICE);

    /**
     * Reference used to clean up subscriptions when the directive is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    ngOnInit(): void {

        this.paginator.page.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((event) => {
            this._state.updatePageIndex(event.pageIndex);
            this._state.updatePageSize(event.pageSize);
        });

        this._state.pageIndex$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((pageIndex) => {
            this.paginator.pageIndex = pageIndex;
        });

        this._state.pageSize$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((pageSize) => {
            this.paginator.pageSize = pageSize;
        });

        this._state.searchTotal$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((total) => {
            this.paginator.length = total;
        });
    }
}
